import React from "react";
import { useNavigate } from "react-router-dom";

import Button from "@/components/ui/Button.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { callFunctionRaw } from "@/lib/callFunction.js";

export default function EhrPasswordReset() {
  const nav = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth() || {};
  const [email, setEmail] = React.useState(String(user?.email || "").trim());
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [showConfirmationHelper, setShowConfirmationHelper] = React.useState(false);

  React.useEffect(() => {
    if (!submitted) {
      setShowConfirmationHelper(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setShowConfirmationHelper(true);
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [submitted]);

  async function handleSubmit(event) {
    event.preventDefault();
    const nextEmail = email.trim();
    if (!nextEmail || submitting) return;

    setSubmitting(true);
    setError("");
    try {
      await callFunctionRaw(
        "ehr-password-reset",
        { action: "request", email: nextEmail },
        { timeoutMs: 20000, skipAuth: true },
      );
      setSubmitted(true);
    } catch {
      setError(
        t(
          "settings.password.reset.requestError",
          "We couldn’t send reset instructions right now. Please try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <SettingsScreenShell>
        <TopBarCenter close onBack={() => nav("/settings/patient/p/ehr")} />

        <div className="mt-5 space-y-6">
          <h1 className="text-headline-small text-on-surface">
            {t("settings.password.reset.confirmTitle", "Check your inbox")}
          </h1>

          <div className="space-y-4 pt-1">
            <Button type="button" fullWidth onClick={() => nav("/settings/patient/p/ehr")}>
              {t("settings.password.reset.backToEhr", "Back to My Health Record")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={() => nav("/settings/patient/login-security")}
            >
              {t("settings.password.reset.backToSecurity", "Back to Account Security")}
            </Button>
          </div>

          {showConfirmationHelper ? (
            <p className="text-body-medium text-on-surface-var">
              {t(
                "settings.password.reset.confirmSubtitle",
                "We sent a password reset email with instructions. Please open the email and follow the secure link to choose a new health record password.",
              )}
            </p>
          ) : null}
        </div>
      </SettingsScreenShell>
    );
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter close onBack={() => nav("/settings/patient/p/ehr")} />

      <form className="mt-5 space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <h1 className="text-headline-small text-on-surface">
            {t(
              "settings.password.reset.title",
              "Reset your health record password",
            )}
          </h1>
          <p className="text-body-medium text-on-surface-var">
            {t(
              "settings.password.reset.subtitle",
              "Enter your email and we’ll send instructions to reset the password used to access your health record.",
            )}
          </p>
        </div>

        <TextField56
          label={t("settings.password.reset.emailLabel", "Email")}
          value={email}
          onChange={setEmail}
          autoComplete="email"
          inputMode="email"
          tone="surface"
        />

        {error ? <p className="text-body-small text-error">{error}</p> : null}

        <Button type="submit" fullWidth disabled={!email.trim()} loading={submitting}>
          {t("settings.password.reset.action", "Send reset instructions")}
        </Button>
      </form>
    </SettingsScreenShell>
  );
}
